import React, { useState, useContext, useEffect } from "react";
import classnames from "classnames";
import { Formik, Field, Form as YupForm } from "formik";
import * as Yup from "yup";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner,
  Badge,
  Form,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Accordion,
  AccordionItem,
  AccordionBody,
  AccordionHeader,
  Container,
} from "reactstrap";
import { useDispatch } from "react-redux";
import ValidationAlertStatic from "../../ValidationAlertStatic";
import {
  useCreateSafetyAppApproveWorkflowMutation,
  useCreateSafetyAppClosedRevokedWorkflowMutation,
  usePostApprovalCommentsMutation,
  usePostApprovalCommentsAttachmentMutation,
} from "slices/workflow/WorkFlowSlice";
import { useUpdateRiskAssessmentPermitMutation } from "slices/riskAssements/riskAssementsSlice";
import { ServiceContext } from "context/ServiceContextProvider";
import { BadgeStatus } from "helpers/badgeStatus";
import { safetyAppSlice } from "slices/safetyApp/safetyappSlice";
import { STATUS_COLORS } from "helpers/common_helper";
import { getErrorText } from "helpers/error_message";
import { Link } from "react-router-dom";
import { IsValidPermissions } from "Routes/AuthProtected";
import { formatDateTime } from "helpers/date_utils";
import { resourceInventoryManagementSlice } from "slices/InventoryManagement/inventoryManagementSlice";
import PriceViewComponent from "pages/ResourceInventory/CommonComponents/PriceViewComponent";

interface ProjectProps {
  permitNo: string;
  workFlowData: any;
  workFlowLoading: any;
  refetchProject: any;
  confirmCloseModal: boolean;
  confirmRevokeModal: boolean;
  toggleConfirmCloseModal: any;
  toggleConfirmRevokeModal: any;
  toggleConfirmCancelModal: any;
  confirmCancelModal: any;
  isLoadingCancelPermit: boolean;
  cancelPermit: any;
  permitType: string;
  setIsPageLoading: any;
  /** Called after approval/close/revoke/cancel so parent can refetch PO (e.g. to show Create Delivery without reload) */
  onAfterApprovalStatusChange?: () => void | Promise<void>;
}

// Helper component for cost display
const CostDisplay: React.FC<{
  totalCost: number;
  approvalCost: number;
  requiresAttention: boolean;
  formattedTotal: string;
  formattedApproval: string;
}> = ({ totalCost, approvalCost, requiresAttention, formattedTotal, formattedApproval }) => {
  if (totalCost === 0 && approvalCost === 0) return null;

  return (
    <div className={`p-2 rounded ${requiresAttention ? 'bg-warning-subtle border border-warning' : 'bg-light'}`}>
      <Row>
        <Col lg={6} sm={6}>
          <div>
            <p className="mb-1 text-uppercase fw-medium small">Total Cost:</p>
            <h6 className="mb-0 fw-bold"><PriceViewComponent price={formattedTotal} /></h6>
          </div>
        </Col>
        <Col lg={6} sm={6}>
          <div>
            <p className="mb-1 text-uppercase fw-medium small">Approval Limit:</p>
            <h6 className="mb-0 fw-bold"><PriceViewComponent price={formattedApproval} /></h6>
          </div>
        </Col>
      </Row>
      {requiresAttention && (
        <div className="mt-2">
          <Badge color="warning" className="w-100">
            <i className="ri-alert-line me-1"></i>
            Requires Higher Approval
          </Badge>
        </div>
      )}
    </div>
  );
};

// Helper function to get cost information
const getCostDisplayInfo = (workflowData: any, workflow: any) => {
  if (workflowData?.type !== "PURCHASE_ORDER") return null;

  // Parse as float and handle any formatting issues
  const totalCost = parseFloat(workflowData?.total_cost?.toString() || "0");
  const approvalCost = parseFloat(workflow?.approval_cost?.toString() || "0");

  // Show attention required when total cost is >= approval limit
  // AND approval limit is greater than 0 (meaning there's a limit set)
  const requiresAttention = totalCost >= approvalCost && approvalCost > 0;

  return {
    totalCost,
    approvalCost,
    requiresAttention,
    formattedTotal: totalCost.toFixed(2),
    formattedApproval: approvalCost.toFixed(2)
  };
};

const canApproveBasedOnCostAndRole = (
  workflowData: any,
  workflow: any,
  selectedRoleName: string | null,
): boolean => {
  // Only apply this logic for Purchase Orders (safety-app permit workflows are unaffected)
  if (workflowData?.type !== "PURCHASE_ORDER") {
    return false;
  }

  const hasMatchingRole = workflow?.role_name?.includes(selectedRoleName);

  // Hierarchy bypass: any approver whose active login role matches a still-pending
  // PO step can approve it directly — they do not have to wait for the lower levels.
  // A higher-level approver (e.g. Managing Director) acting on their step causes the
  // backend (_process_approval) to auto-skip the lower pending levels and finalize.
  // Unauthorized roles are still blocked because the role must match the step.
  return (
    !!hasMatchingRole &&
    workflow?.approval_status === "Pending Approval"
  );
};


const ResourceApprovalOverviewTab: React.FC<ProjectProps> = ({
  permitNo,
  workFlowData,
  workFlowLoading,
  refetchProject,
  confirmCloseModal,
  confirmRevokeModal,
  toggleConfirmCloseModal,
  toggleConfirmRevokeModal,
  toggleConfirmCancelModal,
  confirmCancelModal,
  isLoadingCancelPermit,
  cancelPermit,
  permitType,
  setIsPageLoading,
  onAfterApprovalStatusChange,
}) => {
  const context = useContext(ServiceContext);

  console.log("workflow data in overview", workFlowData);

  if (!context) {
    throw new Error(
      "ProfileDropdown must be used within a ServiceContextProvider",
    );
  }

  const { selectedRoleName } = context;
  const dispatch = useDispatch();

  const [rejectModal, setRejectModal] = useState(false);
  const [nestedRejectModal, setNestedRejectModal] = useState(false);
  const [approvalModal, setApprovalModal] = useState(false);
  const [confirmApproveModal, setConfirmApproveModal] = useState(false); // Confirmation modal for approval
  const [selectedApprovalWorkflow, setSelectedApprovalWorkflow] =
    useState<any>(null);
  const [reason, setReason] = useState("");
  const [overbudgetNotes, setOverbudgetNotes] = useState("");
  const [workflowToReject, setWorkflowToReject] = useState<any>(null);
  const [showValidationAlert, setShowValidationAlert] = useState(true);

  const [activeTab, setactiveTab] = useState("1");

  const [location, setLocation] = useState<{
    latitude: number | null;
    longitude: number | null;
  }>({ latitude: null, longitude: null });
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [modalBackDrop, setModalBackDrop] = useState<boolean>(false);

  // Remove risk assessment related hooks and state
  const [selectedAssessment, setSelectedAssessment] = useState<string | null>(null);

  // Remove risk assessment update mutations
  const [updateReport, { isLoading: isUpdating }] = useUpdateRiskAssessmentPermitMutation();

  const [createApproveWorkflow, { isLoading: createApprovelLoading }] =
    useCreateSafetyAppApproveWorkflowMutation();

  const [createCloseRevokeWorkflow, { isLoading: createCloseRevokeLoading }] =
    useCreateSafetyAppClosedRevokedWorkflowMutation();

  const [postCommentsAction, { isLoading: postCommentsLoading }] =
    usePostApprovalCommentsMutation();
  const [postCommentsAttachment, { isLoading: postCommentsAttachmentLoading }] =
    usePostApprovalCommentsAttachmentMutation();

  const toggleRejectModal = () => setRejectModal(!rejectModal);
  const toggleNestedRejectModal = () =>
    setNestedRejectModal(!nestedRejectModal);
  const toggleApprovalModal = () => setApprovalModal(!approvalModal);
  const toggleConfirmApproveModal = () =>
    setConfirmApproveModal(!confirmApproveModal);

  const handleReject = (workflow: any) => {
    setSelectedApprovalWorkflow(workflow);
    toggleRejectModal();
  };

  const [openAccordionItem, setOpenAccordionItem] = useState<string>("");

  // Function to toggle accordion items
  const toggleAccordion = (id: string) => {
    setOpenAccordionItem((prev) => (prev === id ? "" : id));
  };

  const handleFileDownload = async (url: string) => {
    try {
      // Create a URL object
      const urlObject = new URL(url);
      const pathname = urlObject.pathname;
      let filename = pathname.split("/").pop();

      // Handle the case where filename is undefined
      if (!filename) {
        console.error("Filename could not be extracted from the URL.");
        return; // Optionally handle this case differently
      }

      // Fetch the file
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }

      // Convert response to blob
      const blob = await response.blob();

      // Create an object URL for the blob
      const fileURL = window.URL.createObjectURL(blob);

      // Create an anchor element and trigger the download
      const link = document.createElement("a");
      link.href = fileURL;
      link.download = filename; // Assign a string value
      link.click();

      // Cleanup: Revoke the object URL to free up memory
      window.URL.revokeObjectURL(fileURL);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const getLocation = async () => {
    if ("geolocation" in navigator) {
      setIsPageLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setIsPageLoading(false);
        },
        (err) => {
          setValidationMessages((prev) => {
            const errorMessage = getErrorText("location-disabled");
            return prev.includes(errorMessage) ? prev : [...prev, errorMessage];
          });
          setModalBackDrop(true);
          setIsPageLoading(false);
        },
        { enableHighAccuracy: true },
      );
    } else {
      setValidationMessages((prev) => {
        const errorMessage = getErrorText("location-not-supported");
        return prev.includes(errorMessage) ? prev : [...prev, errorMessage];
      });
      setModalBackDrop(true);
      setIsPageLoading(false);
    }
  };

  const openNestedRejectModal = (workflow: any) => {
    setWorkflowToReject(workflow);
    toggleNestedRejectModal();
  };

  const isRoleAllowed = (next_id: any, workflow: any) => {
    return (
      workflow.role_name.includes(selectedRoleName) &&
      parseInt(next_id) === parseInt(workflow.id)
    );
  };

  const isNextAction = (next_id: any, workflow: any) => {
    return parseInt(next_id) === parseInt(workflow.id);
  };

  const inValidateQuery = async () => {
    switch (permitType) {
      case "HOT_WORK_PERMIT":
        return dispatch(
          safetyAppSlice.util.invalidateTags([
            { type: "GetHotworkPermitByNumber", id: workFlowData.approval_id },
            { type: "GetHotworkPermit" },
          ]),
        );
      case "GENERAL_WORK_PERMIT":
        return dispatch(
          safetyAppSlice.util.invalidateTags([
            {
              type: "GetGeneralworkPermitByNumber",
              id: workFlowData.approval_id,
            },
            { type: "GetGeneralworkPermit" },
          ]),
        );
      case "WORK_AT_HEIGHT_PERMIT":
        return dispatch(
          safetyAppSlice.util.invalidateTags([
            {
              type: "GetWorkAtHeightPermitByNumber",
              id: workFlowData.approval_id,
            },
            { type: "GetWorkAtHeightPermit" },
          ]),
        );
      case "LIFTING_PERMIT":
        return dispatch(
          safetyAppSlice.util.invalidateTags([
            {
              type: "GetLiftingWorkPermitByNumber",
              id: workFlowData.approval_id,
            },
            { type: "GetLiftingWorkPermit" },
          ]),
        );
      case "EXCAVATION_PERMIT":
        return dispatch(
          safetyAppSlice.util.invalidateTags([
            {
              type: "GetExcavationPermitByNumber",
              id: workFlowData.approval_id,
            },
            { type: "GetExcavationPermit" },
          ]),
        );
      case "CONFINED_SPACE_PERMIT":
        return dispatch(
          safetyAppSlice.util.invalidateTags([
            {
              type: "GetConfinedSpacePermitByNumber",
              id: workFlowData.approval_id,
            },
            { type: "GetConfinedSpacePermit" },
          ]),
        );
      case "PIPE_JACKING_PERMIT":
        return dispatch(
          safetyAppSlice.util.invalidateTags([
            {
              type: "GetPipeJackingPermitByNumber",
              id: workFlowData.approval_id,
            },
            { type: "GetPipeJackingPermit" },
          ]),
        );
      case "PILING_PERMIT":
        return dispatch(
          safetyAppSlice.util.invalidateTags([
            {
              type: "GetPillingPermitByNumber",
              id: workFlowData.approval_id,
            },
            { type: "GetPillingPermit" },
          ]),
        );
      case "PURCHASE_ORDER":
        return dispatch(
          resourceInventoryManagementSlice.util.invalidateTags([
            {
              type: "GetPurchaseOrderByNumber",
              id: workFlowData.approval_id,
            },
            { type: "GetPurchaseOrders" },
          ])
        )
      default:
        return;
    }
  };

  const IsEditable = () => {
    return IsValidPermissions(["EDIT_SAFETY_APP_APPROVALS"]);
  };

  const handleSubmitReject = async (e: any) => {
    e.preventDefault();

    if (location.latitude === null || location.longitude === null) {
      alert("Unable to fetch your location. Please try again or Reload Page.");
      return;
    }

    const rejectData = {
      approval_id: selectedApprovalWorkflow.approval_id,
      approval_request_id: selectedApprovalWorkflow.resourceId,
      approval_list_id: selectedApprovalWorkflow?.id,
      approval_status: "Rejected",
      user_comments: reason,
      latitude: location.latitude,
      longitude: location.longitude,
    };

    try {
      await createApproveWorkflow(rejectData).unwrap();
      await refetchProject();
      await inValidateQuery();
      if (onAfterApprovalStatusChange) await onAfterApprovalStatusChange();
      setReason("");
      console.log("Rejection submitted successfully");
    } catch (error) {
      console.error("Error rejecting workflow", error);
    }
    toggleNestedRejectModal();
  };

  // Helper function to check if any previous approver has provided overbudget notes
  const hasPreviousApproverProvidedNotes = () => {
    if (workFlowData?.type !== "PURCHASE_ORDER") return false;

    // Check all approval requests
    const approvalRequests = workFlowData?.resource_approval_request || [];

    for (const request of approvalRequests) {
      const approvals = request?.resource_approval || [];
      // Check all approved approvals (not just current one)
      for (const approval of approvals) {
        // Skip current approval being processed
        if (approval.id === selectedApprovalWorkflow?.id) continue;

        // Check if this approval is approved and has overbudget notes
        if (
          approval.approval_status === "Approved" &&
          approval.overbudget_notes &&
          approval.overbudget_notes.trim() !== ""
        ) {
          return true;
        }
      }
    }

    return false;
  };

  const handleAcceptApproval = (resourceId: any, workflow: any) => {
    console.log("cccc", resourceId, "www", workflow);
    setSelectedApprovalWorkflow({
      ...workflow,
      resourceId: resourceId,
    });
    // Reset overbudget notes when opening modal
    setOverbudgetNotes("");
    toggleConfirmApproveModal();
  };

  const handleRejectApproval = (resourceId: any, workflow: any) => {
    console.log("www", workflow);
    setSelectedApprovalWorkflow({
      ...workflow,
      resourceId: resourceId,
    });
    toggleNestedRejectModal();
  };

  const handleApprove = async (e: any) => {
    e.preventDefault();

    if (location.latitude === null || location.longitude === null) {
      alert("Unable to fetch your location. Please try again or Reload Page.");
      return;
    }

    // Check if this is a PO approval and if overbudget notes are required
    const isPO = workFlowData?.type === "PURCHASE_ORDER";

    if (isPO) {
      // Check if PO is overbudget
      const poTotalCost = parseFloat(workFlowData?.total_cost?.toString() || "0");
      const projectAllocatedBudgetRaw =
        workFlowData?.project_allocated_budget?.toString() ||
        workFlowData?.project?.project_allocated_budget?.toString() ||
        "0";
      // Handle comma-separated numbers
      const projectAllocatedBudget = parseFloat(
        projectAllocatedBudgetRaw.replace(/,/g, "").trim() || "0"
      );
      const isOverbudget = poTotalCost > projectAllocatedBudget && projectAllocatedBudget > 0;

      if (isOverbudget) {
        // Check if any previous approver has already provided notes
        const previousNotesProvided = hasPreviousApproverProvidedNotes();

        // If no previous approver provided notes, current approver must provide them
        if (!previousNotesProvided && overbudgetNotes.trim() === "") {
          alert("Overbudget notes are required. No previous approver has provided notes for this overbudget purchase order.");
          return;
        }
      }
    }

    const approveData = {
      approval_id: selectedApprovalWorkflow.approval_id,
      approval_request_id: selectedApprovalWorkflow.resourceId,
      approval_list_id: selectedApprovalWorkflow?.id,
      user_comments: reason,
      latitude: location.latitude,
      longitude: location.longitude,
      approval_status: "Approved",
      overbudget_notes: isPO && overbudgetNotes.trim() !== "" ? overbudgetNotes : undefined,
    };

    try {
      await createApproveWorkflow(approveData).unwrap();
      await refetchProject();
      await inValidateQuery();
      if (onAfterApprovalStatusChange) await onAfterApprovalStatusChange();
      setReason("");
      setOverbudgetNotes("");
      toggleConfirmApproveModal(); // Close the confirmation modal
      console.log("Approval submitted successfully");
    } catch (error) {
      console.error("Error approving workflow", error);
      toggleConfirmApproveModal(); // Close the confirmation modal
    }
  };

  const handleClosed = async (e: any) => {
    e.preventDefault();

    // Remove risk assessment requirement for all types including PURCHASE_ORDER
    if (!reason) {
      setShowValidationAlert(true);
      return;
    }

    if (location.latitude === null || location.longitude === null) {
      alert("Unable to fetch your location. Please try again or Reload Page.");
      return;
    }

    const approveData = {
      approval_id: workFlowData.approval_id,
      user_comments: reason,
      latitude: location.latitude,
      longitude: location.longitude,
      approval_status: "Closed",
    };

    try {
      await createCloseRevokeWorkflow(approveData).unwrap();
      await refetchProject();
      await inValidateQuery();
      setReason("");
      setSelectedAssessment(null);
      toggleConfirmCloseModal();
      setShowValidationAlert(false)
      console.log("Approval closed successfully");
    } catch (error) {
      console.error("Error approving workflow", error);
      toggleConfirmCloseModal();
    }
  };

  const handleRevoked = async (e: any) => {
    e.preventDefault();

    if (location.latitude === null || location.longitude === null) {
      alert("Unable to fetch your location. Please try again or Reload Page.");
      return;
    }

    const approveData = {
      approval_id: workFlowData.approval_id,
      user_comments: reason,
      latitude: location.latitude,
      longitude: location.longitude,
      approval_status: "Revoked",
    };

    try {
      await createCloseRevokeWorkflow(approveData).unwrap();
      await refetchProject();
      await inValidateQuery();
      if (onAfterApprovalStatusChange) await onAfterApprovalStatusChange();
      setReason("");
      toggleConfirmRevokeModal();
      console.log("Approval revoked successfully");
    } catch (error) {
      console.error("Error revoked workflow", error);
      toggleConfirmRevokeModal();
    }
  };

  const handleCancel = async (e: any) => {
    e.preventDefault();

    if (location.latitude === null || location.longitude === null) {
      alert("Unable to fetch your location. Please try again or Reload Page.");
      return;
    }

    const permitData = {
      user_comments: reason,
      latitude: location.latitude,
      longitude: location.longitude,
    };

    console.log("approveData", permitData);
    console.log("permitNo", permitNo);

    try {
      await cancelPermit({ permitNo, permitData }).unwrap();
      await refetchProject();
      await inValidateQuery();
      if (onAfterApprovalStatusChange) await onAfterApprovalStatusChange();
      setReason("");
      toggleConfirmCancelModal();
      console.log("Approval cancelled successfully");
    } catch (error) {
      console.error("Error approving workflow", error);
      toggleConfirmCancelModal();
    }
  };

  useEffect(() => {
    if (permitNo) {
      setValidationMessages([]);
      getLocation();
    }
  }, [permitNo]);

  const toggle = (tab: any) => {
    if (activeTab !== tab) {
      setactiveTab(tab);
    }
  };

  const postComments = async (values: any, resetForm: any) => {
    const comments = Object.keys(values).reduce((acc: any, key) => {
      const [fieldName] = key.split("-");
      acc[fieldName] = values[key];
      return acc;
    }, {});
    await postCommentsAction(comments);
    resetForm();
  };

  const customAfterStyles = `
    .timeline-2::after {
      height: 100% !important;
    }
  `;

  if (workFlowLoading) {
    return (
      <Card>
        <CardBody>
          <Container fluid>
            <div className="page-content" style={{ height: "80vh" }}>
              {" "}
              {/* Full viewport height */}
              <Container fluid className="h-100">
                <Row className="h-100 d-flex justify-content-center align-items-center">
                  <Col lg={12} className="text-center">
                    <Spinner color="primary"></Spinner> <br /> <br />
                    <span className="text-muted">
                      Fetching Approval Information...
                    </span>
                  </Col>
                </Row>
              </Container>
            </div>
          </Container>
        </CardBody>
      </Card>
    );
  }

  return (
    <React.Fragment>
      <style>{customAfterStyles}</style>

      <>
        {/* High Value Purchase Order Alert */}
        {workFlowData?.type === "PURCHASE_ORDER" && (() => {
          const totalCost = parseFloat(workFlowData?.total_cost?.toString() || "0");
          const currentApprovalCost = workFlowData?.resource_approval_request?.[0]?.resource_approval
            ?.find((w: any) => w.id === parseInt(workFlowData?.next_approval_id))?.approval_cost;
          const approvalLimit = parseFloat(currentApprovalCost?.toString() || "0");

          // Use >= to include equal values
          if (totalCost >= approvalLimit && approvalLimit > 0) {
            return (
              <div className="alert alert-warning mb-3">
                <div className="d-flex align-items-center">
                  <i className="ri-alert-line me-2 fs-4"></i>
                  <div>
                    <h5 className="alert-heading mb-1">High Value Purchase Order</h5>
                    <p className="mb-0">
                      This purchase order (${totalCost.toLocaleString()}) meets or exceeds the current approval limit
                      (${approvalLimit.toLocaleString()}) and requires attention.
                    </p>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Purchase Order Summary Card */}
        {workFlowData?.type === "PURCHASE_ORDER" && (
          <Card className="mb-3 border-primary">
            <CardBody>
              <Row>
                <Col lg={3} sm={6}>
                  <div className="text-center">
                    <p className="mb-1 text-muted">Total Purchase Order Value</p>
                    <h3 className="text-primary mb-0">
                      <PriceViewComponent price={parseFloat(workFlowData?.total_cost || "0")} />
                    </h3>
                  </div>
                </Col>
                <Col lg={3} sm={6}>
                  <div className="text-center">
                    <p className="mb-1 text-muted">Category</p>
                    <h4 className="text-info mb-0">
                      {workFlowData?.category_name || "N/A"}
                    </h4>
                  </div>
                </Col>
                <Col lg={3} sm={6}>
                  <div className="text-center">
                    <p className="mb-1 text-muted">Current Approval Level</p>
                    <h4 className="text-info mb-0">
                      {workFlowData?.resource_approval_request?.[0]?.resource_approval?.find(
                        (w: any) => w.id === parseInt(workFlowData?.next_approval_id)
                      )?.step_name || "N/A"}
                    </h4>
                  </div>
                </Col>
                <Col lg={3} sm={6}>
                  <div className="text-center">
                    <p className="mb-1 text-muted">Status</p>
                    <BadgeStatus
                      value={workFlowData?.overall_approval_status}
                      color={STATUS_COLORS[workFlowData?.overall_approval_status]}
                    />
                  </div>
                </Col>
              </Row>
              {/* Project section limits - show project total limit and section limits; over-budget notes when present */}
              {(workFlowData?.project_allocated_budget != null ||
                workFlowData?.project?.project_allocated_budget != null ||
                (workFlowData?.project_budget_allocations?.length > 0)) && (
                  <Row className="mt-3 pt-3 border-top">
                    <Col lg={12}>
                      <p className="mb-2 text-muted small text-uppercase fw-semibold">Project section limits</p>

                      {/* Over-budget notes: render only when there is real non-empty text */}
                      {(() => {
                        const raw = workFlowData?.overbudget_notes;
                        if (!raw) {
                          return null;
                        }

                        if (Array.isArray(raw)) {
                          const cleaned = raw
                            .map((n: any) => String(n ?? "").trim())
                            .filter((n: string) => n.length > 0);
                          if (cleaned.length === 0) {
                            return null;
                          }
                          return (
                            <div className="mb-2 p-2 rounded bg-danger-subtle border border-danger">
                              <strong className="d-block mb-1 text-danger">Over-budget notes</strong>
                              <div className="text-dark">
                                {cleaned.join(", ")}
                              </div>
                            </div>
                          );
                        }

                        let text = String(raw).trim();

                        // Many APIs send "[]", "null", "undefined" or JSON-encoded arrays here;
                        // treat these as empty unless they actually contain text.
                        if (
                          !text ||
                          text === "[]" ||
                          text === "{}" ||
                          text.toLowerCase() === "null" ||
                          text.toLowerCase() === "undefined"
                        ) {
                          return null;
                        }

                        // If it looks like a JSON array, attempt to parse and clean it.
                        if (text.startsWith("[") && text.endsWith("]")) {
                          try {
                            const arr = JSON.parse(text);
                            if (Array.isArray(arr)) {
                              const cleaned = arr
                                .map((n: any) => String(n ?? "").trim())
                                .filter((n: string) => n.length > 0);
                              if (cleaned.length === 0) {
                                return null;
                              }
                              text = cleaned.join(", ");
                            }
                          } catch {
                            // fall back to raw text
                          }
                        }

                        if (!text) {
                          return null;
                        }

                        return (
                          <div className="mb-2 p-2 rounded bg-danger-subtle border border-danger">
                            <strong className="d-block mb-1 text-danger">Over-budget notes</strong>
                            <div className="text-dark">{text}</div>
                          </div>
                        );
                      })()}
                      <div className="d-flex flex-wrap gap-3 align-items-center">
                        {(workFlowData?.project_allocated_budget != null ||
                          workFlowData?.project?.project_allocated_budget != null) && (
                            <div>
                              <span className="text-muted me-1">Project total limit:</span>
                              <strong>
                                <PriceViewComponent
                                  price={
                                    workFlowData?.project_allocated_budget ??
                                    workFlowData?.project?.project_allocated_budget
                                  }
                                />
                              </strong>
                            </div>
                          )}
                        {workFlowData?.project_budget_allocations?.length > 0 && (
                          <div className="d-flex flex-wrap gap-2 align-items-center">
                            <span className="text-muted small">Section limits:</span>
                            {workFlowData.project_budget_allocations.map((alloc: any) => (
                              <Badge key={alloc.id} color="light" className="text-dark border">
                                {alloc.section_name}: limit{" "}
                                <PriceViewComponent price={alloc.section_allocated_cost ?? 0} />
                                {(alloc.actual_cost != null || alloc.mapped_cost != null) && (
                                  <> · used <PriceViewComponent price={alloc.actual_cost ?? alloc.mapped_cost ?? 0} /></>
                                )}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                )}
            </CardBody>
          </Card>
        )}

        <Card>
          <CardBody>
            <Nav
              tabs
              className="nav nav-tabs nav-tabs-custom nav-primary  mb-3"
            >
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({ active: activeTab === "1" })}
                  onClick={() => {
                    toggle("1");
                  }}
                >
                  Active Approval List{" "}
                  {workFlowData?.resource_approval_request?.filter(
                    (item: any) => item.is_active === true,
                  )?.length > 0 && (
                      <span className="badge bg-info rounded-circle">
                        {
                          workFlowData?.resource_approval_request?.filter(
                            (item: any) => item.is_active === true,
                          )?.length
                        }
                      </span>
                    )}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={classnames({ active: activeTab === "2" })}
                  onClick={() => {
                    toggle("2");
                  }}
                >
                  Rejected Approval List{" "}
                  {workFlowData?.resource_approval_request?.filter(
                    (item: any) => item.is_active === false,
                  )?.length > 0 && (
                      <span className="badge bg-danger rounded-circle">
                        {
                          workFlowData?.resource_approval_request?.filter(
                            (item: any) => item.is_active === false,
                          )?.length
                        }
                      </span>
                    )}
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab} className="text-muted">
              <TabPane tabId="1" id="home">
                {workFlowData?.resource_approval_request?.filter(
                  (item: any) => item.is_active === true,
                )?.length > 0 ? (
                  <>
                    {workFlowData?.resource_approval_request?.map(
                      (resource: any, index: any) => {
                        return (
                          <>
                            {resource?.is_active === true ? (
                              <Col xl={12} lg={12}>
                                <Card>
                                  <h5 className="py-2 px-2">Approvals</h5>
                                  <CardBody style={{ background: "#dfdfdf" }}>
                                    {resource?.resource_approval?.map(
                                      (workflow: any, index: any) => {
                                        const costInfo = getCostDisplayInfo(workFlowData, workflow);

                                        return (
                                          <Row key={workflow.id}>
                                            <div>
                                              <div className="timeline-2">
                                                <div className="timeline-year text-primary">
                                                  <p
                                                    className="shadow"
                                                    style={{
                                                      width: "15%",
                                                      height: "10px",
                                                      borderRadius: "0",
                                                      ...(costInfo?.requiresAttention ? {
                                                        backgroundColor: '#ffc107',
                                                        color: '#000'
                                                      } : {})
                                                    }}
                                                  >
                                                    {workflow?.step_name}
                                                    {costInfo?.requiresAttention && (
                                                      <i className="ri-alert-line ms-1" title="Requires attention"></i>
                                                    )}
                                                  </p>
                                                </div>
                                                <div className="timeline-continue">
                                                  <Row className="timeline-right">
                                                    <Col xs={12}>
                                                      <p className="timeline-date">
                                                        <h5>
                                                          {workflow?.approval_status ? (
                                                            <BadgeStatus
                                                              value={
                                                                workflow?.approval_status
                                                              }
                                                              color={
                                                                STATUS_COLORS[
                                                                workflow
                                                                  ?.approval_status
                                                                ]
                                                              }
                                                            />
                                                          ) : null}
                                                        </h5>
                                                      </p>
                                                    </Col>
                                                    <Col xs={12}>
                                                      <div
                                                        className="timeline-box shadow pt-0"
                                                        style={{
                                                          minWidth: "90%",
                                                          ...(costInfo?.requiresAttention ? {
                                                            border: '2px solid #ffc107',
                                                            backgroundColor: '#fffbf0'
                                                          } : {})
                                                        }}
                                                      >
                                                        <div>
                                                          <div className="text-muted">
                                                            <div className="pt-4">
                                                              <Row>
                                                                <Col
                                                                  lg={3}
                                                                  sm={6}
                                                                >
                                                                  <div>
                                                                    <p className="mb-2 text-uppercase fw-medium">
                                                                      Step Name
                                                                      :
                                                                    </p>
                                                                    <h5 className="fs-15 mb-0">
                                                                      <Badge color="primary">
                                                                        {
                                                                          workflow?.step_name
                                                                        }
                                                                      </Badge>
                                                                    </h5>
                                                                  </div>
                                                                </Col>
                                                                <Col
                                                                  lg={3}
                                                                  sm={6}
                                                                >
                                                                  <div>
                                                                    <p className="mb-2 text-uppercase fw-medium">
                                                                      Role :
                                                                    </p>
                                                                    <h5 className="fs-15 mb-0">
                                                                      {workflow?.role_name?.map(
                                                                        (
                                                                          role: string,
                                                                          i: number,
                                                                        ) => (
                                                                          <span
                                                                            key={
                                                                              i
                                                                            }
                                                                            className="badge text-uppercase bg-info-subtle text-info me-1"
                                                                          >
                                                                            {
                                                                              role
                                                                            }
                                                                          </span>
                                                                        ),
                                                                      )}
                                                                    </h5>
                                                                  </div>
                                                                </Col>
                                                                <Col
                                                                  lg={3}
                                                                  sm={6}
                                                                >
                                                                  <div>
                                                                    <p className="mb-2 text-uppercase fw-medium">
                                                                      {(() => {
                                                                        switch (
                                                                        workflow?.step_name
                                                                        ) {
                                                                          case "Submission":
                                                                            return "Submitted By:";
                                                                          case "Closed":
                                                                            return "Closed By:";
                                                                          case "Rejected":
                                                                            return "Rejected By:";
                                                                          case "Revoked":
                                                                            return "Revoked By:";
                                                                          default:
                                                                            return "Reviewed By:";
                                                                        }
                                                                      })()}
                                                                    </p>
                                                                    <div className="d-flex align-items-center">
                                                                      <div className="flex-shrink-0">
                                                                        {workflow?.approved_by_name ? (
                                                                          workflow?.approved_by_iamge_url ? (
                                                                            <img
                                                                              src={
                                                                                workflow?.approved_by_iamge_url
                                                                              }
                                                                              alt=""
                                                                              className="avatar-xs rounded-circle"
                                                                            />
                                                                          ) : (
                                                                            <div className="flex-shrink-0 avatar-xs me-2">
                                                                              <div className="avatar-title bg-success-subtle text-success rounded-circle fs-13">
                                                                                <b>
                                                                                  {workflow?.approved_by_name.charAt(
                                                                                    0,
                                                                                  )}
                                                                                </b>
                                                                              </div>
                                                                            </div>
                                                                          )
                                                                        ) : null}
                                                                      </div>
                                                                      <div className="flex-grow-1 ms-2 name">
                                                                        <div>
                                                                          <b>
                                                                            {
                                                                              workflow?.approved_by_name
                                                                            }
                                                                          </b>
                                                                        </div>
                                                                        {workflow?.approved_role_name ? (
                                                                          <div className="badge text-uppercase bg-info-subtle text-info me-1">
                                                                            {
                                                                              workflow?.approved_role_name
                                                                            }
                                                                          </div>
                                                                        ) : (
                                                                          ""
                                                                        )}
                                                                      </div>
                                                                    </div>
                                                                  </div>
                                                                </Col>
                                                                <Col
                                                                  lg={3}
                                                                  sm={6}
                                                                >
                                                                  <div>
                                                                    <p className="mb-2 text-uppercase fw-medium">
                                                                      Approval
                                                                      Required :
                                                                    </p>
                                                                    <h5 className="fs-15 mb-0">
                                                                      {workflow?.is_required ? (
                                                                        <>Yes</>
                                                                      ) : (
                                                                        <>No</>
                                                                      )}
                                                                    </h5>
                                                                  </div>
                                                                </Col>
                                                              </Row>

                                                              {/* Cost details for Purchase Orders: PO value, approval limit, and PO-mapped budget section */}
                                                              {workFlowData?.type === "PURCHASE_ORDER" && (
                                                                <div className="mt-3 p-3 rounded border bg-light">
                                                                  <p className="mb-2 text-muted small text-uppercase fw-semibold">Cost details</p>
                                                                  {costInfo && (
                                                                    <Row>
                                                                      <Col lg={12} sm={12}>
                                                                        <CostDisplay
                                                                          totalCost={costInfo.totalCost}
                                                                          approvalCost={costInfo.approvalCost}
                                                                          requiresAttention={costInfo.requiresAttention}
                                                                          formattedTotal={costInfo.formattedTotal}
                                                                          formattedApproval={costInfo.formattedApproval}
                                                                        />
                                                                      </Col>
                                                                    </Row>
                                                                  )}
                                                                  {/* PO-mapped project budget section (chosen at PO creation) */}
                                                                  {(workFlowData?.project_allocated_budget != null ||
                                                                    workFlowData?.project?.project_allocated_budget != null ||
                                                                    (workFlowData?.project_budget_allocations?.length > 0)) && (
                                                                      <Row className="mt-2 pt-2 border-top">
                                                                        <Col lg={12}>
                                                                          {/* <p className="mb-1 text-muted small text-uppercase fw-semibold">Project budget section (PO mapped)</p>
                                                                        {(workFlowData?.overbudget_notes != null && String(workFlowData.overbudget_notes).trim() !== "") && (
                                                                          <div className="mb-2 p-2 rounded bg-danger-subtle border border-danger">
                                                                            <strong className="d-block mb-1 text-danger">Over-budget notes</strong>
                                                                            <div className="text-dark">{workFlowData.overbudget_notes}</div>
                                                                          </div>
                                                                        )} */}
                                                                          <div className="d-flex flex-wrap gap-3 align-items-center">
                                                                            {(workFlowData?.project_allocated_budget != null ||
                                                                              workFlowData?.project?.project_allocated_budget != null) && (
                                                                                <div>
                                                                                  <span className="text-muted me-1">Project total limit:</span>
                                                                                  <strong>
                                                                                    <PriceViewComponent
                                                                                      price={
                                                                                        workFlowData?.project_allocated_budget ??
                                                                                        workFlowData?.project?.project_allocated_budget
                                                                                      }
                                                                                    />
                                                                                  </strong>
                                                                                </div>
                                                                              )}
                                                                            {workFlowData?.project_budget_allocations?.length > 0 && (
                                                                              <div className="d-flex flex-wrap gap-2 align-items-center">
                                                                                {workFlowData.project_budget_allocations.map((alloc: any) => (
                                                                                  <Badge key={alloc.id} color="light" className="text-dark border">
                                                                                    {alloc.section_name}: limit{" "}
                                                                                    <PriceViewComponent price={alloc.section_allocated_cost ?? 0} />
                                                                                    {(alloc.actual_cost != null || alloc.mapped_cost != null) && (
                                                                                      <> · used <PriceViewComponent price={alloc.actual_cost ?? alloc.mapped_cost ?? 0} /></>
                                                                                    )}
                                                                                  </Badge>
                                                                                ))}
                                                                              </div>
                                                                            )}
                                                                          </div>
                                                                        </Col>
                                                                      </Row>
                                                                    )}
                                                                </div>
                                                              )}
                                                            </div>
                                                            {workflow?.approval_datetime ? (
                                                              <div className="pt-3  mt-4 mb-3">
                                                                <Row>
                                                                  <Col
                                                                    lg={4}
                                                                    sm={6}
                                                                  >
                                                                    <div>
                                                                      <p className="mb-2 text-uppercase fw-medium">
                                                                        Approved
                                                                        Date :
                                                                      </p>
                                                                      <h5 className="fs-15 mb-0">
                                                                        {formatDateTime(
                                                                          workflow?.approval_datetime,
                                                                          "datetime",
                                                                        )}
                                                                      </h5>
                                                                    </div>
                                                                  </Col>
                                                                  <Col
                                                                    lg={4}
                                                                    sm={6}
                                                                  >
                                                                    <div>
                                                                      <p className="mb-2 text-uppercase fw-medium">
                                                                        Action :
                                                                      </p>
                                                                      <h5 className="fs-15 mb-0">
                                                                        {
                                                                          workflow?.comments
                                                                        }
                                                                      </h5>
                                                                    </div>
                                                                  </Col>
                                                                  {/* Comment out Location field */}
                                                                  {/* <Col
                                                                    lg={4}
                                                                    sm={6}
                                                                  >
                                                                    <div>
                                                                      <p className="mb-2 text-uppercase fw-medium">
                                                                        Location
                                                                        :
                                                                      </p>
                                                                      <h5 className="fs-15 mb-0">
                                                                        {
                                                                          workflow?.location
                                                                        }
                                                                      </h5>
                                                                    </div>
                                                                  </Col> */}
                                                                </Row>
                                                              </div>
                                                            ) : (
                                                              ""
                                                            )}

                                                            {workflow?.user_comments &&
                                                              workflow
                                                                ?.user_comments
                                                                .length > 0 ? (
                                                              <div className="pt-3 border-top border-top-dashed mt-0">
                                                                <Row>
                                                                  <Col
                                                                    lg={12}
                                                                    sm={12}
                                                                  >
                                                                    <div>
                                                                      <p className="mb-3 text-uppercase fw-medium">
                                                                        Comments
                                                                        :
                                                                      </p>
                                                                      {workflow?.user_comments?.map(
                                                                        (
                                                                          comment: any,
                                                                          index: number,
                                                                        ) => {
                                                                          return (
                                                                            <>
                                                                              <div className="d-flex mb-1">
                                                                                <div className="flex-shrink-0">
                                                                                  {comment?.commented_by_full_name ? (
                                                                                    comment?.commented_by_image_url ? (
                                                                                      <img
                                                                                        src={
                                                                                          comment?.commented_by_image_url
                                                                                        }
                                                                                        alt=""
                                                                                        className="avatar-xs rounded-circle"
                                                                                      />
                                                                                    ) : (
                                                                                      <div className="flex-shrink-0 avatar-xs me-2">
                                                                                        <div className="avatar-title bg-success-subtle text-success rounded-circle fs-13">
                                                                                          <b>
                                                                                            {comment?.commented_by_full_name.charAt(
                                                                                              0,
                                                                                            )}
                                                                                          </b>
                                                                                        </div>
                                                                                      </div>
                                                                                    )
                                                                                  ) : null}
                                                                                </div>
                                                                                <div className="flex-grow-1 ms-3">
                                                                                  <h5 className="fs-13">
                                                                                    {
                                                                                      comment?.commented_by_full_name
                                                                                    }{" "}
                                                                                    <small className="text-muted ms-2">
                                                                                      {
                                                                                        comment?.comments_datetime_text
                                                                                      }
                                                                                    </small>
                                                                                  </h5>
                                                                                  <p className="text-muted fs-14">
                                                                                    {
                                                                                      comment?.user_comments
                                                                                    }
                                                                                  </p>
                                                                                </div>
                                                                              </div>
                                                                            </>
                                                                          );
                                                                        },
                                                                      )}
                                                                    </div>
                                                                  </Col>
                                                                </Row>
                                                              </div>
                                                            ) : null}

                                                            {workflow?.user_attachment &&
                                                              workflow
                                                                ?.user_attachment
                                                                .length > 0 ? (
                                                              <div className="pt-3 border-top border-top-dashed mt-0">
                                                                <Row className="g-2 mb-1">
                                                                  <p className="mb-2 text-uppercase fw-medium">
                                                                    Attachments
                                                                    :
                                                                  </p>
                                                                  {workflow?.user_attachment?.map(
                                                                    (
                                                                      attachment: any,
                                                                      index: number,
                                                                    ) => {
                                                                      const url =
                                                                        new URL(
                                                                          attachment?.file,
                                                                        );
                                                                      const fileName =
                                                                        url.pathname
                                                                          .split(
                                                                            "/",
                                                                          )
                                                                          .pop();
                                                                      return (
                                                                        <div
                                                                          style={{
                                                                            width:
                                                                              "200px",
                                                                          }}
                                                                          className="d-flex me-2 border border-dashed p-2 rounded position-relative"
                                                                        >
                                                                          <div className="flex-shrink-0 avatar-xs">
                                                                            <div className="avatar-title bg-danger-subtle text-danger fs-15 rounded">
                                                                              <i className="ri-file-zip-line"></i>
                                                                            </div>
                                                                          </div>
                                                                          <div className="flex-shrink-0"></div>
                                                                          <div className="flex-grow-1 overflow-hidden ms-2">
                                                                            <h6 className="text-truncate mb-0">
                                                                              <Link
                                                                                to="#"
                                                                                onClick={() =>
                                                                                  handleFileDownload(
                                                                                    attachment.file_url,
                                                                                  )
                                                                                }
                                                                                className="stretched-link"
                                                                              >
                                                                                {
                                                                                  fileName
                                                                                }
                                                                              </Link>
                                                                            </h6>
                                                                            <small>
                                                                              {
                                                                                attachment?.uploaded_at_text
                                                                              }
                                                                            </small>
                                                                          </div>
                                                                        </div>
                                                                      );
                                                                    },
                                                                  )}
                                                                </Row>
                                                              </div>
                                                            ) : null}

                                                            {IsEditable() &&
                                                              (workflow?.approval_datetime ||
                                                                (isNextAction(workFlowData?.next_approval_id, workflow) ||
                                                                  canApproveBasedOnCostAndRole(workFlowData, workflow, selectedRoleName))) ? (
                                                              <Row>
                                                                <div className="pt-3 border-top border-top-dashed mt-3">
                                                                  <Formik
                                                                    key={index} // Key for the Formik instance
                                                                    initialValues={{
                                                                      [`user_comments-${index}`]:
                                                                        "",
                                                                      [`approval_list_id-${index}`]:
                                                                        workflow.id,
                                                                    }}
                                                                    validationSchema={Yup.object(
                                                                      {
                                                                        [`user_comments-${index}`]:
                                                                          Yup.string().required(
                                                                            "Comment is required",
                                                                          ),
                                                                      },
                                                                    )}
                                                                    onSubmit={(
                                                                      values,
                                                                      {
                                                                        resetForm,
                                                                      },
                                                                    ) =>
                                                                      postComments(
                                                                        values,
                                                                        resetForm,
                                                                      )
                                                                    }
                                                                  >
                                                                    {({
                                                                      errors,
                                                                      touched,
                                                                    }) => (
                                                                      <YupForm className="mt-0">
                                                                        <Row className="g-3">
                                                                          <Col
                                                                            xs={
                                                                              12
                                                                            }
                                                                          >
                                                                            <div
                                                                              className="d-flex"
                                                                              style={{
                                                                                justifyContent:
                                                                                  "space-between",
                                                                              }}
                                                                            >
                                                                              <label
                                                                                htmlFor={`user_comments-${index}`}
                                                                                className="form-label text-body"
                                                                              >
                                                                                Add
                                                                                Comments
                                                                              </label>
                                                                              <span className="text-end">
                                                                                <button
                                                                                  type="button"
                                                                                  disabled={
                                                                                    postCommentsAttachmentLoading
                                                                                  }
                                                                                  onClick={() =>
                                                                                    // @ts-ignore
                                                                                    document
                                                                                      .getElementById(
                                                                                        `file-input-${index}`,
                                                                                      )
                                                                                      .click()
                                                                                  }
                                                                                  className="btn btn-ghost-secondary btn-icon waves-effect me-1 shadow-none"
                                                                                >
                                                                                  {postCommentsAttachmentLoading ? (
                                                                                    <span className="d-flex align-items-center">
                                                                                      <>
                                                                                        <Spinner size="sm" />
                                                                                      </>
                                                                                    </span>
                                                                                  ) : (
                                                                                    <i className="ri-attachment-line fs-16"></i>
                                                                                  )}
                                                                                </button>

                                                                                <Button
                                                                                  type="submit"
                                                                                  className="btn btn-sm btn-info"
                                                                                  disabled={
                                                                                    postCommentsLoading
                                                                                  }
                                                                                >
                                                                                  {postCommentsLoading ? (
                                                                                    <span className="d-flex align-items-center">
                                                                                      <>
                                                                                        <Spinner size="sm" />
                                                                                        &nbsp;Loading....
                                                                                      </>
                                                                                    </span>
                                                                                  ) : (
                                                                                    <>
                                                                                      Post
                                                                                      Comments
                                                                                    </>
                                                                                  )}
                                                                                </Button>
                                                                              </span>
                                                                            </div>
                                                                            <Field
                                                                              as="textarea"
                                                                              className={`form-control bg-light border-light ${errors[
                                                                                `comments-${index}`
                                                                              ] &&
                                                                                touched[
                                                                                `comments-${index}`
                                                                                ]
                                                                                ? "is-invalid"
                                                                                : ""
                                                                                }`}
                                                                              id={`user_comments-${index}`}
                                                                              name={`user_comments-${index}`} // Unique name for each textarea
                                                                              rows={
                                                                                2
                                                                              }
                                                                              placeholder="Enter your comment..."
                                                                            />

                                                                            {/* Hidden File Input */}
                                                                            <input
                                                                              id={`file-input-${index}`}
                                                                              name={`attachment`}
                                                                              type="file"
                                                                              className="d-none"
                                                                              onChange={async (
                                                                                e,
                                                                              ) => {
                                                                                const fileInput =
                                                                                  e.currentTarget;
                                                                                if (
                                                                                  fileInput &&
                                                                                  fileInput.files
                                                                                ) {
                                                                                  const file =
                                                                                    fileInput
                                                                                      .files[0];
                                                                                  if (
                                                                                    file
                                                                                  ) {
                                                                                    console.log(
                                                                                      "File workflow:",
                                                                                      workflow.id,
                                                                                    );
                                                                                    const attachment =
                                                                                    {
                                                                                      file: file,
                                                                                      approval_list_id:
                                                                                        workflow.id,
                                                                                    };
                                                                                    await postCommentsAttachment(
                                                                                      attachment,
                                                                                    );
                                                                                    // Handle the file here (e.g., upload or set to Formik state)
                                                                                    console.log(
                                                                                      "File selected:",
                                                                                      file,
                                                                                    );
                                                                                    // For example, you could use setFieldValue if you want to add it to Formik values
                                                                                    // setFieldValue(`attachment-${index}`, file);
                                                                                  }
                                                                                }
                                                                              }}
                                                                            />

                                                                            {/* Hidden Field for approval list ID */}
                                                                            <Field
                                                                              type="hidden"
                                                                              name={`approval_list_id-${index}`}
                                                                              value={
                                                                                workflow.id
                                                                              }
                                                                            />
                                                                            {errors[
                                                                              `user_comments-${index}`
                                                                            ] &&
                                                                              touched[
                                                                              `user_comments-${index}`
                                                                              ] && (
                                                                                <div className="invalid-feedback">
                                                                                  {String(
                                                                                    errors[
                                                                                    `user_comments-${index}`
                                                                                    ],
                                                                                  )}
                                                                                </div>
                                                                              )}

                                                                            {/* Hidden Field */}
                                                                            <Field
                                                                              type="hidden"
                                                                              name={`approval_list_id-${index}`}
                                                                              value={
                                                                                workflow.id
                                                                              }
                                                                            />
                                                                          </Col>
                                                                        </Row>
                                                                      </YupForm>
                                                                    )}
                                                                  </Formik>
                                                                </div>
                                                              </Row>
                                                            ) : (
                                                              <></>
                                                            )}

                                                            {IsEditable() &&
                                                              workflow?.approval_status ===
                                                              "Pending Approval" &&
                                                              (isNextAction(workFlowData?.next_approval_id, workflow) ||
                                                                canApproveBasedOnCostAndRole(workFlowData, workflow, selectedRoleName)) ? (
                                                              <div className="pt-3 border-top border-top-dashed mt-4">
                                                                <Row>
                                                                  <Col
                                                                    lg={12}
                                                                    sm={12}
                                                                  >
                                                                    <div
                                                                      className="d-flex"
                                                                      style={{
                                                                        justifyContent:
                                                                          "flex-end",
                                                                      }}
                                                                    >
                                                                      <Button
                                                                        color="success"
                                                                        onClick={() =>
                                                                          handleAcceptApproval(
                                                                            resource.id,
                                                                            workflow,
                                                                          )
                                                                        }
                                                                        className="btn sm me-2"
                                                                        disabled={
                                                                          !(isRoleAllowed(workFlowData?.next_approval_id, workflow) ||
                                                                            canApproveBasedOnCostAndRole(workFlowData, workflow, selectedRoleName)) ||
                                                                          createApprovelLoading
                                                                        }
                                                                      >
                                                                        Approve
                                                                      </Button>
                                                                      <Button
                                                                        color="danger"
                                                                        onClick={() =>
                                                                          handleRejectApproval(
                                                                            resource.id,
                                                                            workflow,
                                                                          )
                                                                        }
                                                                        className="btn me-2"


                                                                        disabled={
                                                                          !(isRoleAllowed(workFlowData.next_approval_id, workflow) ||
                                                                            canApproveBasedOnCostAndRole(workFlowData, workflow, selectedRoleName))
                                                                        }
                                                                      >
                                                                        Reject
                                                                      </Button>
                                                                    </div>
                                                                  </Col>
                                                                </Row>
                                                              </div>
                                                            ) : (
                                                              <></>
                                                            )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </Col>
                                                  </Row>
                                                </div>
                                              </div>
                                            </div>
                                          </Row>
                                        );
                                      },
                                    )}
                                  </CardBody>
                                </Card>
                              </Col>
                            ) : (
                              <div className="text-center">
                                No Active Approvals
                              </div>
                            )}
                          </>
                        );
                      },
                    )}
                  </>
                ) : (
                  <div className="text-center">No Active Approvals</div>
                )}
              </TabPane>

              <TabPane tabId="2" id="product">
                {/* Rejected Approvals Tab - Similar modifications can be applied here if needed */}
                {workFlowData?.resource_approval_request?.filter(
                  (item: any) => item.is_active === false,
                )?.length > 0 ? (
                  <>
                    {workFlowData?.resource_approval_request?.map(
                      (resource: any, index: any) => {
                        return (
                          <>
                            {resource?.is_active === false ? (
                              <Col xl={12} lg={12}>
                                <Accordion
                                  open={openAccordionItem}
                                  toggle={toggleAccordion}
                                  className="custom-accordionwithicon custom-accordion-border accordion-border-box accordion-secondary"
                                >
                                  <AccordionItem key={index}>
                                    <AccordionHeader
                                      className="d-flex align-items-center"
                                      targetId={`${index}`}
                                    >
                                      <h4
                                        className="card-title flex-grow-1 mb-0 d-flex"
                                        style={{
                                          justifyContent: "space-between",
                                        }}
                                      >
                                        <div>
                                          <span className="text-muted">
                                            Purchase Order No:
                                          </span>{" "}
                                          {resource?.approval_id}
                                        </div>
                                        <div>
                                          {
                                            resource?.approval_request_datetime_text
                                          }
                                        </div>
                                        <h5 className="text-end">
                                          {resource?.approval_request_status ? (
                                            <BadgeStatus
                                              value={
                                                resource?.approval_request_status
                                              }
                                              color={
                                                STATUS_COLORS[
                                                resource
                                                  ?.approval_request_status
                                                ]
                                              }
                                            />
                                          ) : null}
                                        </h5>
                                      </h4>
                                      &nbsp;&nbsp;&nbsp;
                                    </AccordionHeader>
                                    <AccordionBody accordionId={`${index}`}>
                                      <Card>
                                        <CardBody
                                          style={{ background: "#dfdfdf" }}
                                        >
                                          {resource?.resource_approval?.map(
                                            (workflow: any, index: any) => {
                                              const costInfo = getCostDisplayInfo(workFlowData, workflow);

                                              return (
                                                <Row key={workflow.id}>
                                                  <div>
                                                    <div className="timeline-2">
                                                      <div className="timeline-year text-primary">
                                                        <p
                                                          className="shadow"
                                                          style={{
                                                            ...(costInfo?.requiresAttention ? {
                                                              backgroundColor: '#ffc107',
                                                              color: '#000'
                                                            } : {})
                                                          }}
                                                        >
                                                          Level{" "}
                                                          {
                                                            workflow?.approval_level
                                                          }
                                                          {costInfo?.requiresAttention && (
                                                            <i className="ri-alert-line ms-1" title="Requires attention"></i>
                                                          )}
                                                        </p>
                                                      </div>
                                                      <div className="timeline-continue">
                                                        <Row className="timeline-right">
                                                          <Col xs={12}>
                                                            <p className="timeline-date">
                                                              <h5>
                                                                {workflow?.approval_status ? (
                                                                  <BadgeStatus
                                                                    value={
                                                                      workflow?.approval_status
                                                                    }
                                                                    color={
                                                                      STATUS_COLORS[
                                                                      workflow
                                                                        ?.approval_status
                                                                      ]
                                                                    }
                                                                  />
                                                                ) : null}
                                                              </h5>
                                                            </p>
                                                          </Col>
                                                          <Col xs={12}>
                                                            <div
                                                              className="timeline-box shadow pt-0"
                                                              style={{
                                                                minWidth: "90%",
                                                                ...(costInfo?.requiresAttention ? {
                                                                  border: '2px solid #ffc107',
                                                                  backgroundColor: '#fffbf0'
                                                                } : {})
                                                              }}
                                                            >
                                                              <div>
                                                                <div className="text-muted">
                                                                  <div className="pt-4">
                                                                    <Row>
                                                                      <Col
                                                                        lg={3}
                                                                        sm={6}
                                                                      >
                                                                        <div>
                                                                          <p className="mb-2 text-uppercase fw-medium">
                                                                            Step
                                                                            Name
                                                                            :
                                                                          </p>
                                                                          <h5 className="fs-15 mb-0">
                                                                            <Badge color="primary">
                                                                              {
                                                                                workflow?.step_name
                                                                              }
                                                                            </Badge>
                                                                          </h5>
                                                                        </div>
                                                                      </Col>
                                                                      <Col
                                                                        lg={3}
                                                                        sm={6}
                                                                      >
                                                                        <div>
                                                                          <p className="mb-2 text-uppercase fw-medium">
                                                                            Role
                                                                            :
                                                                          </p>
                                                                          <h5 className="fs-15 mb-0">
                                                                            {workflow?.role_name?.map(
                                                                              (
                                                                                role: string,
                                                                                i: number,
                                                                              ) => (
                                                                                <span
                                                                                  key={
                                                                                    i
                                                                                  }
                                                                                  className="badge text-uppercase bg-info-subtle text-info me-1"
                                                                                >
                                                                                  {
                                                                                    role
                                                                                  }
                                                                                </span>
                                                                              ),
                                                                            )}
                                                                          </h5>
                                                                        </div>
                                                                      </Col>
                                                                      <Col
                                                                        lg={3}
                                                                        sm={6}
                                                                      >
                                                                        <div>
                                                                          <p className="mb-2 text-uppercase fw-medium">
                                                                            Reviewed
                                                                            By :
                                                                          </p>
                                                                          <div className="d-flex align-items-center">
                                                                            <div className="flex-shrink-0">
                                                                              {workflow?.approved_by_name ? (
                                                                                workflow?.approved_by_iamge_url ? (
                                                                                  <img
                                                                                    src={
                                                                                      workflow?.approved_by_iamge_url
                                                                                    }
                                                                                    alt=""
                                                                                    className="avatar-xs rounded-circle"
                                                                                  />
                                                                                ) : (
                                                                                  <div className="flex-shrink-0 avatar-xs me-2">
                                                                                    <div className="avatar-title bg-success-subtle text-success rounded-circle fs-13">
                                                                                      <b>
                                                                                        {workflow?.approved_by_name.charAt(
                                                                                          0,
                                                                                        )}
                                                                                      </b>
                                                                                    </div>
                                                                                  </div>
                                                                                )
                                                                              ) : null}
                                                                            </div>
                                                                            <div className="flex-grow-1 ms-2 name">
                                                                              <b>
                                                                                {
                                                                                  workflow?.approved_by_name
                                                                                }
                                                                              </b>
                                                                            </div>
                                                                          </div>
                                                                        </div>
                                                                      </Col>
                                                                      <Col
                                                                        lg={3}
                                                                        sm={6}
                                                                      >
                                                                        <div>
                                                                          <p className="mb-2 text-uppercase fw-medium">
                                                                            Approval
                                                                            Required
                                                                            :
                                                                          </p>
                                                                          <h5 className="fs-15 mb-0">
                                                                            {workflow?.approval_required ? (
                                                                              <>
                                                                                Yes
                                                                              </>
                                                                            ) : (
                                                                              <>
                                                                                No
                                                                              </>
                                                                            )}
                                                                          </h5>
                                                                        </div>
                                                                      </Col>
                                                                    </Row>

                                                                    {/* Cost details for Purchase Orders in Rejected tab: PO value, approval limit, PO-mapped budget section */}
                                                                    {workFlowData?.type === "PURCHASE_ORDER" && (
                                                                      <div className="mt-3 p-3 rounded border bg-light">
                                                                        <p className="mb-2 text-muted small text-uppercase fw-semibold">Cost details</p>
                                                                        {costInfo && (
                                                                          <Row>
                                                                            <Col lg={12} sm={12}>
                                                                              <CostDisplay
                                                                                totalCost={costInfo.totalCost}
                                                                                approvalCost={costInfo.approvalCost}
                                                                                requiresAttention={costInfo.requiresAttention}
                                                                                formattedTotal={costInfo.formattedTotal}
                                                                                formattedApproval={costInfo.formattedApproval}
                                                                              />
                                                                            </Col>
                                                                          </Row>
                                                                        )}
                                                                        {/* PO-mapped project budget section */}
                                                                        {(workFlowData?.project_allocated_budget != null ||
                                                                          workFlowData?.project?.project_allocated_budget != null ||
                                                                          (workFlowData?.project_budget_allocations?.length > 0)) && (
                                                                            <Row className="mt-2 pt-2 border-top">
                                                                              <Col lg={12}>
                                                                                {workFlowData?.overbudget_notes &&
                                                                                  (
                                                                                    (Array.isArray(workFlowData.overbudget_notes) && workFlowData.overbudget_notes.length > 0) ||
                                                                                    (!Array.isArray(workFlowData.overbudget_notes) && String(workFlowData.overbudget_notes).trim() !== "")
                                                                                  ) && (
                                                                                    <div className="mb-2 p-2 rounded bg-danger-subtle border border-danger">
                                                                                      <strong className="d-block mb-1 text-danger">Over-budget notes</strong>
                                                                                      <div className="text-dark">
                                                                                        {Array.isArray(workFlowData.overbudget_notes)
                                                                                          ? workFlowData.overbudget_notes.join(", ")
                                                                                          : workFlowData.overbudget_notes}
                                                                                      </div>
                                                                                    </div>
                                                                                  )}
                                                                                <div className="d-flex flex-wrap gap-3 align-items-center">
                                                                                  {(workFlowData?.project_allocated_budget != null ||
                                                                                    workFlowData?.project?.project_allocated_budget != null) && (
                                                                                      <div>
                                                                                        <span className="text-muted me-1">Project total limit:</span>
                                                                                        <strong>
                                                                                          <PriceViewComponent
                                                                                            price={
                                                                                              workFlowData?.project_allocated_budget ??
                                                                                              workFlowData?.project?.project_allocated_budget
                                                                                            }
                                                                                          />
                                                                                        </strong>
                                                                                      </div>
                                                                                    )}
                                                                                  {workFlowData?.project_budget_allocations?.length > 0 && (
                                                                                    <div className="d-flex flex-wrap gap-2 align-items-center">
                                                                                      {workFlowData.project_budget_allocations.map((alloc: any) => (
                                                                                        <Badge key={alloc.id} color="light" className="text-dark border">
                                                                                          {alloc.section_name}: limit{" "}
                                                                                          <PriceViewComponent price={alloc.section_allocated_cost ?? 0} />
                                                                                          {(alloc.actual_cost != null || alloc.mapped_cost != null) && (
                                                                                            <> · used <PriceViewComponent price={alloc.actual_cost ?? alloc.mapped_cost ?? 0} /></>
                                                                                          )}
                                                                                        </Badge>
                                                                                      ))}
                                                                                    </div>
                                                                                  )}
                                                                                </div>
                                                                              </Col>
                                                                            </Row>
                                                                          )}
                                                                      </div>
                                                                    )}
                                                                  </div>
                                                                  <div className="pt-3  mt-4 mb-3">
                                                                    <Row>
                                                                      <Col
                                                                        lg={4}
                                                                        sm={6}
                                                                      >
                                                                        <div>
                                                                          <p className="mb-2 text-uppercase fw-medium">
                                                                            Approved
                                                                            Date
                                                                            :
                                                                          </p>
                                                                          <h5 className="fs-15 mb-0">
                                                                            {
                                                                              workflow?.approval_datetime_text
                                                                            }
                                                                          </h5>
                                                                        </div>
                                                                      </Col>
                                                                      <Col
                                                                        lg={4}
                                                                        sm={6}
                                                                      >
                                                                        <div>
                                                                          <p className="mb-2 text-uppercase fw-medium">
                                                                            Action
                                                                            :
                                                                          </p>
                                                                          <h5 className="fs-15 mb-0">
                                                                            {
                                                                              workflow?.comments
                                                                            }
                                                                          </h5>
                                                                        </div>
                                                                      </Col>
                                                                      {/* Comment out Location field */}
                                                                      {/* <Col
                                                                        lg={4}
                                                                        sm={6}
                                                                      >
                                                                        <div>
                                                                          <p className="mb-2 text-uppercase fw-medium">
                                                                            Location
                                                                            :
                                                                          </p>
                                                                          <h5 className="fs-15 mb-0">
                                                                            {
                                                                              workflow?.location
                                                                            }
                                                                          </h5>
                                                                        </div>
                                                                      </Col> */}
                                                                    </Row>
                                                                  </div>
                                                                  {workflow?.user_comments &&
                                                                    workflow
                                                                      ?.user_comments
                                                                      .length >
                                                                    0 ? (
                                                                    <div className="pt-3 border-top border-top-dashed mt-0">
                                                                      <Row>
                                                                        <Col
                                                                          lg={
                                                                            12
                                                                          }
                                                                          sm={
                                                                            12
                                                                          }
                                                                        >
                                                                          <div>
                                                                            <p className="mb-4 text-uppercase fw-medium">
                                                                              Comments
                                                                              :
                                                                            </p>
                                                                            {workflow?.user_comments?.map(
                                                                              (
                                                                                comment: any,
                                                                                index: number,
                                                                              ) => {
                                                                                return (
                                                                                  <>
                                                                                    <div className="d-flex mb-1">
                                                                                      <div className="flex-shrink-0">
                                                                                        {comment?.commented_by_full_name ? (
                                                                                          comment?.commented_by_image_url ? (
                                                                                            <img
                                                                                              src={
                                                                                                comment?.commented_by_image_url
                                                                                              }
                                                                                              alt=""
                                                                                              className="avatar-xs rounded-circle"
                                                                                            />
                                                                                          ) : (
                                                                                            <div className="flex-shrink-0 avatar-xs me-2">
                                                                                              <div className="avatar-title bg-success-subtle text-success rounded-circle fs-13">
                                                                                                <b>
                                                                                                  {comment?.commented_by_full_name.charAt(
                                                                                                    0,
                                                                                                  )}
                                                                                                </b>
                                                                                              </div>
                                                                                            </div>
                                                                                          )
                                                                                        ) : null}
                                                                                      </div>
                                                                                      <div className="flex-grow-1 ms-3">
                                                                                        <h5 className="fs-13">
                                                                                          {
                                                                                            comment?.commented_by_full_name
                                                                                          }{" "}
                                                                                          <small className="text-muted ms-2">
                                                                                            {
                                                                                              comment?.comments_datetime_text
                                                                                            }
                                                                                          </small>
                                                                                        </h5>
                                                                                        <p className="text-muted fs-14">
                                                                                          {
                                                                                            comment?.user_comments
                                                                                          }
                                                                                        </p>
                                                                                      </div>
                                                                                    </div>
                                                                                  </>
                                                                                );
                                                                              },
                                                                            )}
                                                                          </div>
                                                                        </Col>
                                                                      </Row>
                                                                    </div>
                                                                  ) : null}
                                                                  {workflow?.user_attachment &&
                                                                    workflow
                                                                      ?.user_attachment
                                                                      .length >
                                                                    0 ? (
                                                                    <div className="pt-3 border-top border-top-dashed mt-0">
                                                                      <Row className="g-2 mb-2">
                                                                        <p className="mb-2 text-uppercase fw-medium">
                                                                          Attachments
                                                                          :
                                                                        </p>
                                                                        {workflow?.user_attachment?.map(
                                                                          (
                                                                            attachment: any,
                                                                            index: number,
                                                                          ) => {
                                                                            const url =
                                                                              new URL(
                                                                                attachment?.file,
                                                                              );
                                                                            const fileName =
                                                                              url.pathname
                                                                                .split(
                                                                                  "/",
                                                                                )
                                                                                .pop();
                                                                            return (
                                                                              <div
                                                                                style={{
                                                                                  width:
                                                                                    "200px",
                                                                                }}
                                                                                className="d-flex me-2 border border-dashed p-2 rounded position-relative"
                                                                              >
                                                                                <div className="flex-shrink-0 avatar-xs">
                                                                                  <div className="avatar-title bg-danger-subtle text-danger fs-15 rounded">
                                                                                    <i className="ri-file-zip-line"></i>
                                                                                  </div>
                                                                                </div>
                                                                                <div className="flex-shrink-0"></div>
                                                                                <div className="flex-grow-1 overflow-hidden ms-2">
                                                                                  <h6 className="text-truncate mb-0">
                                                                                    <Link
                                                                                      to="#"
                                                                                      onClick={() =>
                                                                                        handleFileDownload(
                                                                                          attachment.file_url,
                                                                                        )
                                                                                      }
                                                                                      className="stretched-link"
                                                                                    >
                                                                                      {
                                                                                        fileName
                                                                                      }
                                                                                    </Link>
                                                                                  </h6>
                                                                                  <small>
                                                                                    {
                                                                                      attachment?.uploaded_at_text
                                                                                    }
                                                                                  </small>
                                                                                </div>
                                                                              </div>
                                                                            );
                                                                          },
                                                                        )}
                                                                      </Row>
                                                                    </div>
                                                                  ) : null}
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </Col>
                                                        </Row>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </Row>
                                              );
                                            },
                                          )}
                                        </CardBody>
                                      </Card>
                                    </AccordionBody>
                                  </AccordionItem>
                                </Accordion>
                              </Col>
                            ) : (
                              <></>
                            )}
                          </>
                        );
                      },
                    )}
                  </>
                ) : (
                  <div className="text-center">No Rejected Approvals</div>
                )}
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </>

      {/* All your existing modals remain the same */}
      {/* Confirm Approve Modal */}
      <Modal
        backdrop={"static"}
        isOpen={confirmApproveModal}
        toggle={toggleConfirmApproveModal}
      >
        <Form onSubmit={(e) => handleApprove(e)}>
          <ModalHeader>Confirm Approval</ModalHeader>
          <ModalBody>
            <p>
              Are you sure you want to approve Action{" "}
              <b color="text-primary">{selectedApprovalWorkflow?.step_name}</b>?
            </p>
            <FormGroup>
              <Label for="approvalComment">Comment</Label>
              <Input
                required
                type="textarea"
                name="approvalComment"
                id="approvalComment"
                placeholder="Add a comment"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </FormGroup>
            {/* Show overbudget notes field for PO approvals when overbudget */}
            {(() => {
              const isPO = workFlowData?.type === "PURCHASE_ORDER";

              if (!isPO) return null;

              // Check if PO is overbudget
              // Try multiple possible paths for project_allocated_budget
              const poTotalCost = parseFloat(workFlowData?.total_cost?.toString() || "0");
              const projectAllocatedBudgetRaw =
                workFlowData?.project_allocated_budget?.toString() ||
                workFlowData?.project?.project_allocated_budget?.toString() ||
                "0";

              // Handle comma-separated numbers (e.g., "1,000,000")
              const projectAllocatedBudget = parseFloat(
                projectAllocatedBudgetRaw.replace(/,/g, "").trim() || "0"
              );

              const isOverbudget = poTotalCost > projectAllocatedBudget && projectAllocatedBudget > 0;

              // Only show field if PO is overbudget
              if (!isOverbudget) {
                return null;
              }

              // Check if any previous approver has already provided notes
              const previousNotesProvided = hasPreviousApproverProvidedNotes();

              // If previous approver provided notes, show info message but don't require notes
              if (previousNotesProvided) {
                return (
                  <FormGroup className="mt-3">
                    <div className="alert alert-info mb-0">
                      <small>
                        <i className="ri-information-line me-1"></i>
                        A previous approver has already provided overbudget notes. You may add additional notes below (optional).
                      </small>
                    </div>
                    <Label for="overbudgetNotes" className="mt-2">
                      Additional Overbudget Notes (Optional)
                    </Label>
                    <Input
                      type="textarea"
                      name="overbudgetNotes"
                      id="overbudgetNotes"
                      placeholder="You may add additional notes if needed..."
                      rows={3}
                      value={overbudgetNotes}
                      onChange={(e) => setOverbudgetNotes(e.target.value)}
                    />
                  </FormGroup>
                );
              }

              // No previous notes provided, require notes from current approver
              return (
                <FormGroup className="mt-3">
                  <Label for="overbudgetNotes">
                    Overbudget Notes <span className="text-danger">*</span>
                  </Label>
                  <Input
                    required
                    type="textarea"
                    name="overbudgetNotes"
                    id="overbudgetNotes"
                    placeholder="Please provide notes explaining why this overbudget purchase order should be approved..."
                    rows={4}
                    value={overbudgetNotes}
                    onChange={(e) => setOverbudgetNotes(e.target.value)}
                  />
                  <div className="alert alert-warning mt-2 mb-0">
                    <small>
                      <i className="ri-alert-line me-1"></i>
                      This PO (${poTotalCost.toLocaleString()}) exceeds the project allocated budget (${projectAllocatedBudget.toLocaleString()}).
                      Notes are required for approval. Once you provide notes, other approvers won't need to provide them.
                    </small>
                  </div>
                </FormGroup>
              );
            })()}
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              color="success"
              disabled={createApprovelLoading}
            >
              {createApprovelLoading ? (
                <Spinner size="sm" />
              ) : (
                "Confirm Approval"
              )}
            </Button>
            <Button
              color="secondary"
              onClick={toggleConfirmApproveModal}
              disabled={createApprovelLoading}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Confirm Close Modal */}
      <Modal
        backdrop={"static"}
        isOpen={confirmCloseModal}
        toggle={toggleConfirmCloseModal}
      >
        <Form onSubmit={(e) => handleClosed(e)}>
          <ModalHeader>Close Report</ModalHeader>
          <ModalBody>

            <p>
              Are you sure you want to Close the Report{" "}
              <b color="text-primary">
                {selectedApprovalWorkflow?.approval_id}
              </b>
              ?
            </p>

            {showValidationAlert && (
              <div className="alert alert-danger" role="alert">
                Please provide a reason
              </div>
            )}

            <FormGroup>
              <Label for="approvalComment">Add Comment to Close Report</Label>
              <Input
                required
                type="textarea"
                name="approvalComment"
                id="approvalComment"
                placeholder="Add a comment to Close Report"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              color="success"
              disabled={createCloseRevokeLoading}
            >
              {createCloseRevokeLoading ? (
                <Spinner size="sm" />
              ) : (
                "Close Permit"
              )}
            </Button>
            <Button
              color="secondary"
              onClick={toggleConfirmCloseModal}
              disabled={createCloseRevokeLoading}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Confirm Revoke Modal */}
      <Modal
        backdrop={"static"}
        isOpen={confirmRevokeModal}
        toggle={toggleConfirmRevokeModal}
      >
        <Form onSubmit={(e) => handleRevoked(e)}>
          <ModalHeader>Revoke Report</ModalHeader>
          <ModalBody>
            <p>
              Are you sure you want to Revoke the Report{" "}
              <b color="text-primary">
                {selectedApprovalWorkflow?.approval_id}
              </b>
              ?
            </p>
            <FormGroup>
              <Label for="approvalComment">Add Comment to Revoke Report</Label>
              <Input
                required
                type="textarea"
                name="approvalComment"
                id="approvalComment"
                placeholder="Add a comment to Close Report"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              color="success"
              disabled={createCloseRevokeLoading}
            >
              {createCloseRevokeLoading ? (
                <Spinner size="sm" />
              ) : (
                "Revoke Permit"
              )}
            </Button>
            <Button
              color="secondary"
              onClick={toggleConfirmRevokeModal}
              disabled={createCloseRevokeLoading}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Nested Reject Modal */}
      <Modal
        backdrop={"static"}
        isOpen={nestedRejectModal}
        toggle={toggleNestedRejectModal}
      >
        <Form onSubmit={(e) => handleSubmitReject(e)}>
          <ModalHeader>Confirm Rejection</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="reason">Reason for Rejection</Label>
              <Input
                type="textarea"
                name="reason"
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              type="submit"
              disabled={createApprovelLoading}
            >
              {createApprovelLoading ? (
                <Spinner size="sm" />
              ) : (
                <> Confirm Rejection</>
              )}
            </Button>
            <Button
              color="secondary"
              onClick={toggleNestedRejectModal}
              disabled={createApprovelLoading}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Confirm Cancel Modal */}
      <Modal
        backdrop={"static"}
        isOpen={confirmCancelModal}
        toggle={toggleConfirmCancelModal}
      >
        <Form onSubmit={(e) => handleCancel(e)}>
          <ModalHeader>Cancel Permit</ModalHeader>
          <ModalBody>
            <p>
              Are you sure you want to Cancel the Permit{" "}
              <b color="text-primary">{permitNo}</b>?
            </p>
            <FormGroup>
              <Label for="approvalComment">Add Comment to Cancel Permit</Label>
              <Input
                required
                type="textarea"
                name="approvalComment"
                id="approvalComment"
                placeholder="Add a comment to Cancel Report"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              color="success"
              disabled={isLoadingCancelPermit}
            >
              {isLoadingCancelPermit ? <Spinner size="sm" /> : "Cancel Permit"}
            </Button>
            <Button
              color="secondary"
              onClick={toggleConfirmCancelModal}
              disabled={isLoadingCancelPermit}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <ValidationAlertStatic
        modalBackDrop={modalBackDrop}
        setModalBackDrop={setModalBackDrop}
        validationMessages={validationMessages}
        previousPageNavLink="/homeworkpermit-list"
      />
    </React.Fragment>
  );
};

export default ResourceApprovalOverviewTab;